import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories, mainSiteUrl } from '@/mocks/categories';
import { articlePreviews } from '@/mocks/articles';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [guideDropdownOpen, setGuideDropdownOpen] = useState(false);
  const [mobileGuidesOpen, setMobileGuidesOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const closeAll = () => {
    setMobileOpen(false);
    setDropdownOpen(false);
    setGuideDropdownOpen(false);
    setMobileGuidesOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-background-50/50 backdrop-blur-sm'
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link
            to="/"
            onClick={closeAll}
            className="flex items-center gap-2 text-foreground-900 hover:text-primary-500 transition-colors cursor-pointer"
          >
            <span className="font-heading text-lg md:text-xl font-bold whitespace-nowrap">
              SugargooSpreadsheet<span className="text-primary-500">.link</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/"
              onClick={closeAll}
              className="text-sm font-medium text-foreground-800 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
            >
              Home
            </Link>

            <div className="relative">
              <button
                onClick={() => { setDropdownOpen(!dropdownOpen); setGuideDropdownOpen(false); }}
                className="text-sm font-medium text-foreground-800 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1"
              >
                Categories
                <i className={`ri-arrow-down-s-line text-xs transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {dropdownOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setDropdownOpen(false)}></div>
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-56 bg-background-50 border border-background-200/70 rounded-xl shadow-lg py-2 max-h-80 overflow-y-auto z-20">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        {cat.name}
                      </a>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="relative">
              <button
                onClick={() => { setGuideDropdownOpen(!guideDropdownOpen); setDropdownOpen(false); }}
                className="text-sm font-medium text-foreground-800 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1"
              >
                Shopping Guide
                <i className={`ri-arrow-down-s-line text-xs transition-transform ${guideDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {guideDropdownOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setGuideDropdownOpen(false)}></div>
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-72 bg-background-50 border border-background-200/70 rounded-xl shadow-lg py-2 max-h-80 overflow-y-auto z-20">
                    {articlePreviews.map((article) => (
                      <Link
                        key={article.slug}
                        to={`/guide/${article.slug}`}
                        onClick={() => setGuideDropdownOpen(false)}
                        className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
                      >
                        <span className="line-clamp-1">{article.title}</span>
                      </Link>
                    ))}
                  </div>
                </>
              )}
            </div>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-1.5 px-5 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Buy It Now
              <i className="ri-arrow-right-up-line text-sm"></i>
            </a>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer text-foreground-900"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 px-4 py-4 flex flex-col gap-3 max-h-[75vh] overflow-y-auto">
          <Link
            to="/"
            onClick={closeAll}
            className="text-sm font-medium text-foreground-800 hover:text-primary-500 transition-colors py-2 cursor-pointer flex items-center gap-2"
          >
            <i className="ri-home-4-line text-base"></i>
            Home
          </Link>

          <div className="py-2 border-t border-background-100">
            <p className="text-xs text-foreground-500 mb-3 uppercase tracking-wide">Categories</p>
            <div className="grid grid-cols-2 gap-1.5">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-500 transition-colors py-2 px-2 rounded-lg hover:bg-primary-50 cursor-pointer"
                >
                  <span className="w-7 h-7 flex items-center justify-center rounded-md bg-background-200 flex-shrink-0">
                    <i className="ri-price-tag-3-line text-xs text-foreground-500"></i>
                  </span>
                  <span className="whitespace-nowrap text-xs">{cat.name}</span>
                </a>
              ))}
            </div>
          </div>

          <div className="py-2 border-t border-background-100">
            <button
              onClick={() => setMobileGuidesOpen(!mobileGuidesOpen)}
              className="w-full flex items-center justify-between text-sm font-medium text-foreground-800 hover:text-primary-500 transition-colors py-2 cursor-pointer"
            >
              <span className="flex items-center gap-2">
                <i className="ri-book-open-line text-base"></i>
                Shopping Guide
              </span>
              <i className={`ri-arrow-down-s-line text-sm transition-transform ${mobileGuidesOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {mobileGuidesOpen && (
              <div className="mt-2 ml-7 flex flex-col gap-1 max-h-52 overflow-y-auto">
                {articlePreviews.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/guide/${article.slug}`}
                    onClick={closeAll}
                    className="text-xs text-foreground-600 hover:text-primary-500 transition-colors py-1.5 cursor-pointer line-clamp-1"
                  >
                    {article.title}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center justify-center gap-1.5 px-5 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer mt-1"
          >
            Buy It Now
            <i className="ri-arrow-right-up-line text-sm"></i>
          </a>
        </div>
      )}
    </nav>
  );
}